import React, { FC, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormGroup,
  FormText,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import { DealerArea } from '../../../shared/dealer';
import {
  ActionButtonModal,
  PageHeader,
  PermissionBoundary,
  UserStateComponent,
} from '../../components';
import { useTranslation } from '../../translations';
import {
  Fetcher,
  useBoolState,
  useCheckboxState,
  useFetcher,
  useForm,
  useInputState,
} from '../../utils';

export const DealerAreas: FC = () => {
  const { ts } = useTranslation();

  return (
    <UserStateComponent>
      <PageHeader>{ts('dealer_areas')}</PageHeader>
      <DealerAreaContainer />
    </UserStateComponent>
  );
};

const DealerAreaContainer: FC = () => {
  const [openModal, enableModal, disableModal] = useBoolState(false);
  const [areaToEdit, setAreaToEdit] = useState<DealerArea | undefined>(undefined);

  const { ts } = useTranslation();
  const fetcher = useFetcher(async () => {
    return await api.getDealerAreas();
  }, []);

  if (!fetcher.success) {
    return <Fetcher result={fetcher} />;
  }

  return (
    <>
      {openModal && (
        <NewDealerAreaEditModal
          area={areaToEdit}
          disableModal={disableModal}
          refresh={fetcher.refresh}
        />
      )}
      <div>
        <Button
          color="primary"
          id="createArea"
          onClick={() => {
            setAreaToEdit(undefined);
            enableModal();
          }}
        >
          {ts('create_new_dealer_area')}
        </Button>
      </div>
      <hr />
      <Row className="justify-content-center" id="dealerAreaList">
        {fetcher.data.length === 0 && (
          <Col xs={12}>
            {/* eslint-disable-next-line react/jsx-no-literals */}
            <p className="text-center">Nothing here yet!</p>
          </Col>
        )}
        {fetcher.data.map((area) => (
          <Col className="mb-10" id={area.name} key={area.name} lg={9} xs={9}>
            <Card>
              <CardBody>
                <h4 className="card-title">{area.name} </h4>
                {area.description}
                <div className="float-end">
                  <PermissionBoundary inline requiredPermissions="vendor:update">
                    <Button
                      color="primary"
                      id={`editArea${area.id}`}
                      onClick={() => {
                        setAreaToEdit(area);
                        enableModal();
                      }}
                    >
                      {ts('common.edit')}
                    </Button>{' '}
                  </PermissionBoundary>
                  <PermissionBoundary inline requiredPermissions="staff:admin:update">
                    <ActionButtonModal
                      actionContent="common.delete"
                      buttonContent="common.delete"
                      color="danger"
                      id={`deleteArea${area.id}`}
                      onComplete={async () => {
                        await api.deleteDealerArea(area.id);
                        toast.success(ts('deleted_area'));
                        fetcher.refresh();
                      }}
                      title="delete_dealer_area"
                    >
                      {ts('dealer.are_you_sure_you_want_to_delete', area.name)}
                      <strong>{ts('this_will_delete_all_applications')}</strong>
                    </ActionButtonModal>
                  </PermissionBoundary>
                </div>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>
    </>
  );
};

const NewDealerAreaEditModal: FC<{
  readonly area?: DealerArea;
  readonly disableModal: () => void;
  readonly refresh: () => void;
}> = ({ disableModal, refresh, area }) => {
  const fetcher = useFetcher(async () => {
    return await api.getProductCategories();
  }, []);

  const [areaName, setAreaName] = useInputState(area?.name ?? '');
  const [areaDescription, setAreaDescription] = useInputState(area?.description ?? '');
  const [areaOpen, setAreaOpen] = useCheckboxState(area?.open ?? false);
  const [areaEnabled, setAreaEnabled] = useCheckboxState(area?.enabled ?? false);
  const [areaFull, setAreaFull] = useCheckboxState(area?.full ?? false);
  const [areaCatId, setAreaCatId] = useState(area?.categoryId);

  const categoryIdRef = useRef<HTMLInputElement>(null);

  const isCreate = area === undefined;

  const { ts } = useTranslation();

  const defaultArea = fetcher.data?.find((c) => c.isVendor)?.id;
  const form = useForm(async () => {
    const categoryId = areaCatId ?? defaultArea;
    if (!categoryId) {
      return;
    }

    const payload = {
      name: areaName,
      description: areaDescription,
      enabled: areaEnabled,
      full: areaFull,
      open: areaOpen,
      categoryId,
    };

    if (isCreate) {
      await api.createDealerArea(payload);

      toast.success(ts('created_area'));
    } else {
      await api.updateDealerArea(area.id, payload);

      toast.success(ts('updated_area'));
    }

    disableModal();
    refresh();
  }, [
    areaCatId,
    defaultArea,
    isCreate,
    disableModal,
    refresh,
    areaName,
    areaDescription,
    areaEnabled,
    areaFull,
    areaOpen,
    ts,
    area?.id,
  ]);

  return (
    <Modal id="createDealerModal" isOpen>
      <Form onSubmit={form.onSubmit}>
        <ModalHeader>{ts(isCreate ? 'common.create_new' : 'common.edit')}</ModalHeader>
        <ModalBody>
          <FormGroup row>
            <Label for="name" lg={5} sm={12}>
              {ts('name')}
            </Label>
            <Col lg={7} sm={12}>
              <Input defaultValue={area?.name} id="name" name="name" onChange={setAreaName} />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label for="description" lg={5} sm={12}>
              {ts('description')}
            </Label>
            <Col lg={7} sm={12}>
              <Input
                defaultValue={area?.description}
                id="description"
                name="description"
                onChange={setAreaDescription}
                type="textarea"
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label for="categoryId" lg={5} sm={12}>
              {ts('common.product_category')}
            </Label>

            {fetcher.data && (
              <Col lg={7} sm={12}>
                <Input
                  defaultValue={area?.categoryId.toString()}
                  id="categoryId"
                  innerRef={categoryIdRef}
                  name="categoryId"
                  onChange={(e) => {
                    setAreaCatId(Number.parseInt(e.currentTarget.value, 10));
                  }}
                  type="select"
                >
                  {fetcher.data
                    .filter((t) => t.isVendor)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} ({c.id})
                      </option>
                    ))}
                </Input>
              </Col>
            )}
          </FormGroup>
          <FormGroup check className="start-align mt-10" row>
            <Input
              defaultChecked={area?.enabled}
              id="enabledArea"
              name="enabled"
              onChange={setAreaEnabled}
              type="checkbox"
            />
            <Label check for="enabledArea">
              {ts('enabled')}
            </Label>
            <FormText color="muted">{ts('display_the_option_to_users')}</FormText>
          </FormGroup>
          <FormGroup check className="start-align mt-10" row>
            <Input
              defaultChecked={area?.full}
              id="fullArea"
              name="full"
              onChange={setAreaFull}
              type="checkbox"
            />
            <Label check for="fullArea">
              {ts('full')}
            </Label>
            <FormText color="muted">{ts('show_that_registrations_is_full')}</FormText>
          </FormGroup>
          <FormGroup check className="start-align mt-10" row>
            <Input
              defaultChecked={area?.open}
              id="openArea"
              name="open"
              onChange={setAreaOpen}
              type="checkbox"
            />
            <Label check for="openArea">
              {ts('open')}
            </Label>
            <FormText color="muted">{ts('allow_for_additional_registrations')}</FormText>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={disableModal}>
            {ts('common.cancel')}
          </Button>{' '}
          <Button color="primary" id="createDealerArea" type="submit">
            {ts(isCreate ? 'common.create' : 'common.edit')}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};
